import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Building2, Users, FileText, ClipboardList, Wrench, Zap, KeyRound, CornerDownLeft, History, X, ArrowLeft } from 'lucide-react';
import { flattenMenu, matchFeatures, loadRecents, saveRecent, clearRecents } from './searchFeatures';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_META = {
  zuletzt:    { label: 'Zuletzt geöffnet', icon: History },
  funktionen: { label: 'Funktionen', icon: Zap },
  objekte:    { label: 'Objekte',     icon: Building2 },
  personen:   { label: 'Personen',    icon: Users },
  dokumente:  { label: 'Dokumente',   icon: FileText },
  vorgaenge:  { label: 'Vorgänge',    icon: ClipboardList },
  bewerbungen:{ label: 'Mietbewerbungen', icon: KeyRound },
  handwerker: { label: 'Handwerker',  icon: Wrench },
};

const SearchResults = ({ sections, flat, active, setActive, onPick, loading, q, results, recentsMode, onClearRecents, mobile }) => {
  const total = flat.length;
  const showEmpty = q.trim().length >= 2 && !loading && total === 0 && results;
  let idx = -1;
  return (
    <>
      {total === 0 && loading && !results && <div className="p-6 text-center text-sm text-gray-400">Suche läuft...</div>}
      {showEmpty && <div className="p-6 text-center text-sm text-gray-400" data-testid="global-search-empty">Keine Treffer für „{q}".</div>}
      {total > 0 && (
        <div className="divide-y divide-gray-100">
          {sections.map(([key, items]) => {
            const meta = SECTION_META[key] || { label: key, icon: FileText };
            const Icon = meta.icon;
            return (
              <div key={key} className="py-2">
                <div className="flex items-center justify-between gap-2 px-4 py-1.5">
                  <span className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-brand-gold" />
                    <span className="text-[10px] font-bold tracking-widest text-brand-gold uppercase">{meta.label} ({items.length})</span>
                  </span>
                  {key === 'zuletzt' && (
                    <button type="button" onClick={onClearRecents} data-testid="global-search-clear-recents" className="text-[11px] text-gray-400 hover:text-gray-600 hover:underline">Verlauf löschen</button>
                  )}
                </div>
                {items.map((it) => {
                  idx += 1;
                  const isActive = idx === active;
                  const RowIcon = it.icon || (it.section && SECTION_META[it.section]?.icon) || Icon;
                  return (
                    <button
                      key={`${key}-${it.id || it.url}`}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      data-active={isActive ? 'true' : undefined}
                      onMouseEnter={() => !mobile && setActive(flat.indexOf(it))}
                      onClick={() => onPick(it, key)}
                      data-testid={`global-search-result-${key}-${it.id || it.url}`}
                      className={`w-full text-left px-4 flex items-start gap-3 transition-colors ${mobile ? 'py-3' : 'py-2'} ${isActive && !mobile ? 'bg-brand-navy/5' : 'hover:bg-gray-50'}`}
                    >
                      <RowIcon className={`w-4 h-4 mt-0.5 shrink-0 ${key === 'funktionen' ? 'text-brand-navy/60' : 'text-gray-300'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-brand-navy truncate">{it.title}</p>
                        {it.subtitle && <p className="text-xs text-gray-400 truncate">{it.subtitle}</p>}
                      </div>
                      {isActive && !mobile ? (
                        <CornerDownLeft className="w-3.5 h-3.5 text-gray-300 mt-1 shrink-0" aria-hidden="true" />
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider text-gray-300 mt-1 shrink-0">{it.type}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
      {!mobile && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-400 flex items-center justify-between">
          <span>{recentsMode ? 'Schnellzugriff' : total > 0 ? `${total} Treffer` : 'Live-Suche'}{loading && total > 0 ? ' · lädt…' : ''}</span>
          <span className="flex items-center gap-2">
            <span><kbd className="px-1 py-0.5 bg-gray-200 rounded text-gray-500">↑↓</kbd> wählen</span>
            <span><kbd className="px-1 py-0.5 bg-gray-200 rounded text-gray-500">↵</kbd> öffnen</span>
            <span><kbd className="px-1 py-0.5 bg-gray-200 rounded text-gray-500">ESC</kbd> schließen</span>
          </span>
        </div>
      )}
    </>
  );
};

/**
 * GlobalSearchInline
 * Suchleiste im Portal-Header (Desktop) + Lupen-Button mit Vollbild-Suche (Mobil).
 * Funktionen/Seiten sofort aus dem Menü, Daten live über /api/search/global, zuletzt geöffnete Treffer als Schnellzugriff.
 */
const GlobalSearchInline = ({ menu = [], isStaff = false }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState(loadRecents);
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const mobileInputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const features = useMemo(() => flattenMenu(menu, isStaff), [menu, isStaff]);
  const featureHits = useMemo(() => matchFeatures(features, q), [features, q]);
  const recentsMode = q.trim().length < 2;

  const updatePos = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 8, left: r.left, width: Math.max(r.width, 420) });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open, updatePos]);

  const runSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/search/global?q=${encodeURIComponent(query)}&limit_per_type=5`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setResults(await r.json());
    } catch (e) {
      // Suche ist sekundär – Fehler still ignorieren
    } finally {
      setLoading(false);
    }
  }, [token]);

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    setOpen(true);
    setActive(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 250);
  };

  useEffect(() => {
    const onClick = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inWrap && !inDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const closeAll = () => { setOpen(false); setMobileOpen(false); setQ(''); setResults(null); };
  const onPick = (item, sectionKey) => {
    if (sectionKey !== 'zuletzt') setRecents(saveRecent({ ...item, section: sectionKey }));
    closeAll();
    // Ältere „Zuletzt“-Einträge zeigten nur auf die Kundenliste – direkt zum Kundenprofil
    const url = (item.url === '/portal/admin/kunden' && item.id && (sectionKey === 'personen' || item.section === 'personen'))
      ? `/portal/admin/kunden/${item.id}` : item.url;
    if (url) navigate(url);
  };
  const onClearRecents = () => { setRecents(clearRecents()); setOpen(false); };

  // Sektionen: Schnellzugriff (leeres Feld) ODER Funktionen zuerst, dann Daten
  const sections = useMemo(() => {
    if (recentsMode) return recents.length ? [['zuletzt', recents]] : [];
    const out = [];
    if (featureHits.length) out.push(['funktionen', featureHits.map((f) => ({ id: f.path, title: f.label, subtitle: f.group, type: f.group === 'Aktion' ? 'Aktion' : 'Seite', url: f.path, icon: f.icon }))]);
    Object.entries(results?.results || {}).forEach(([k, items]) => { if (items?.length) out.push([k, items]); });
    return out;
  }, [recentsMode, recents, featureHits, results]);
  const flat = useMemo(() => sections.flatMap(([, items]) => items), [sections]);
  const total = flat.length;
  useEffect(() => { if (active >= total) setActive(0); }, [total, active]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setMobileOpen(false); e.target.blur(); return; }
    if (!total) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => (a + 1) % total); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + total) % total); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = flat[active] || flat[0];
      if (hit) onPick(hit, sections.find(([, items]) => items.includes(hit))?.[0]);
    }
  };

  useEffect(() => {
    if (!open) return;
    dropdownRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  useEffect(() => {
    if (mobileOpen) setTimeout(() => mobileInputRef.current?.focus(), 50);
  }, [mobileOpen]);

  const isMac = typeof navigator !== 'undefined' && /Mac|iPad|iPhone|iPod/.test(navigator.platform || '');
  const shortcutLabel = isMac ? '⌘K' : 'Strg K';

  useEffect(() => {
    const handler = (e) => {
      const cmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (!cmdK) return;
      e.preventDefault();
      const input = wrapRef.current?.querySelector('input[data-testid="global-search-input"]');
      if (input && input.offsetParent !== null) {
        input.focus();
        input.select();
        setOpen(true);
      } else {
        setMobileOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const showDropdown = open && !mobileOpen && pos && (q.trim().length >= 2 || (recentsMode && recents.length > 0));
  const placeholder = isStaff ? 'Objekte, Personen, Vorgänge, Funktionen suchen …' : 'Dokumente, Vorgänge, Funktionen suchen …';
  const listProps = { sections, flat, active, setActive, onPick, loading, q, results, recentsMode, onClearRecents };

  return (
    <>
      {/* Mobil: Lupen-Button → Vollbild-Suche */}
      {/* Lupen-Button: mobil + schmale Laptops (lg), dort öffnet er die Vollbild-Suche – ab xl direkt eingeben */}
      <button type="button" onClick={() => setMobileOpen(true)} aria-label="Suche öffnen" data-testid="global-search-mobile-btn"
        className="md:hidden lg:block xl:hidden p-2 text-gray-500 hover:text-brand-navy rounded-lg hover:bg-gray-100">
        <Search className="w-5 h-5" />
      </button>

      <div ref={wrapRef} className="relative hidden md:block lg:hidden xl:block w-52 xl:w-40 2xl:w-56 3xl:w-80" data-testid="global-search-wrap">
        <div className={`group flex items-center gap-2.5 pl-3.5 pr-2 h-10 rounded-full border transition-all duration-200 ${open ? 'bg-white border-brand-navy/20' : 'bg-brand-navy/[0.04] border-brand-navy/10 hover:border-brand-navy/25 hover:bg-white'}`}>
          <Search className={`w-4 h-4 shrink-0 transition-colors ${open ? 'text-brand-gold' : 'text-brand-navy/50 group-hover:text-brand-navy'}`} />
          <input
            type="text"
            value={q}
            onChange={onChange}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            aria-label="Globale Suche"
            data-testid="global-search-input"
            style={{ outline: 'none', boxShadow: 'none' }}
            className="flex-1 outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none shadow-none bg-transparent text-sm text-brand-navy placeholder:text-brand-navy/40 min-w-0"
          />
          {loading && <Loader2 className="w-4 h-4 text-brand-gold animate-spin shrink-0" />}
          {!loading && q && (
            <button type="button" onClick={() => { setQ(''); setResults(null); }} aria-label="Suche leeren" data-testid="global-search-clear"
              className="p-1 rounded-full text-brand-navy/40 hover:text-brand-navy hover:bg-brand-navy/5 shrink-0"><X className="w-3.5 h-3.5" /></button>
          )}
          {!loading && !q && (
            <kbd aria-hidden="true" data-testid="global-search-kbd-hint"
              className="hidden lg:inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-brand-navy/10 text-[10px] font-semibold tracking-wide text-brand-navy/50 shrink-0">
              {shortcutLabel}
            </kbd>
          )}
        </div>

        {showDropdown && createPortal(
          <div
            ref={dropdownRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 2147483000 }}
            className="bg-white border border-gray-200 rounded-xl shadow-2xl max-h-[70vh] overflow-y-auto"
            data-testid="global-search-dropdown"
            role="listbox"
          >
            <SearchResults {...listProps} />
          </div>,
          document.body
        )}
      </div>

      {/* Mobil: Vollbild-Suche */}
      {mobileOpen && createPortal(
        <div className="fixed inset-0 bg-white flex flex-col" style={{ zIndex: 2147483000 }} data-testid="global-search-mobile" role="dialog" aria-modal="true" aria-label="Suche">
          <div className="flex items-center gap-2 px-3 h-14 border-b border-gray-200 shrink-0 bg-white pt-[env(safe-area-inset-top)]">
            <button type="button" onClick={closeAll} aria-label="Suche schließen" data-testid="global-search-mobile-close" className="p-2 -ml-1 text-gray-500 hover:text-brand-navy rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center gap-2 px-3 h-10 bg-gray-50 border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-brand-gold/40 focus-within:bg-white">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                ref={mobileInputRef}
                type="text"
                value={q}
                onChange={onChange}
                onKeyDown={onKeyDown}
                placeholder="Suchen…"
                aria-label="Globale Suche"
                data-testid="global-search-mobile-input"
                enterKeyHint="go"
                className="flex-1 outline-none bg-transparent text-base text-gray-800 placeholder:text-gray-400 min-w-0"
              />
              {loading ? <Loader2 className="w-4 h-4 text-brand-gold animate-spin shrink-0" /> : q && (
                <button type="button" onClick={() => { setQ(''); setResults(null); mobileInputRef.current?.focus(); }} aria-label="Eingabe löschen" data-testid="global-search-mobile-clear" className="p-0.5 text-gray-400"><X className="w-4 h-4" /></button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
            {recentsMode && !recents.length ? (
              <div className="p-8 text-center text-sm text-gray-400">
                <Search className="w-8 h-8 mx-auto mb-3 text-gray-200" />
                Suchen Sie nach {isStaff ? 'Funktionen, Objekten, Personen, Dokumenten, Vorgängen und Mietbewerbungen' : 'Funktionen, Dokumenten und Vorgängen'}.
                <p className="mt-1 text-xs">Mindestens 2 Zeichen eingeben.</p>
              </div>
            ) : (
              <SearchResults {...listProps} mobile />
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GlobalSearchInline;
